"use client";

import { c as _c } from "react/compiler-runtime";
import type { FUSportsLivescoreEvent } from "fansunited-data-layer";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { formatDateDDMM, formatTimeHHMM } from "@/utilities/formatting/date";
import type { LivescoreSport } from "@/views/livescore/utils/livescore";
import { formatFootballLiveMinute } from "@/views/livescore/utils/livescore";
interface Props {
  event: FUSportsLivescoreEvent;
  sport: LivescoreSport;
  /**
   * Prepend the kickoff date (DD.MM) as a small muted line. Useful in
   * contexts spanning multiple days (post-body match lists, upcoming
   * fixtures across weeks). Livescore's own day-scoped views leave this off.
   */
  showDate?: boolean;
  /**
   * When a FINISHED match ended after regulation, replace the "FT" / "Fin"
   * label with this string (e.g. "AET", "AP"). Ignored for non-finished
   * statuses. Callers derive the code from the score's period keys
   * (events endpoint) or from `event.winner.reason` (event-by-id).
   */
  postRegLabel?: string | null;
}
export function MatchTimeColumn(t0) {
  const $ = _c(33);
  const {
    event,
    sport,
    showDate: t1,
    postRegLabel: t2
  } = t0;
  const showDate = t1 === undefined ? false : t1;
  const postRegLabel = t2 === undefined ? null : t2;
  const labels = useLabels();
  const statusType = event.status?.type?.toUpperCase();
  let t3;
  let timeStr;
  if ($[0] !== event.startTime || $[1] !== showDate) {
    const kickoff = event.startTime ? new Date(event.startTime) : null;
    timeStr = kickoff ? formatTimeHHMM(kickoff) : null;
    t3 = showDate && kickoff ? formatDateDDMM(kickoff) : null;
    $[0] = event.startTime;
    $[1] = showDate;
    $[2] = t3;
    $[3] = timeStr;
  } else {
    t3 = $[2];
    timeStr = $[3];
  }
  const dateStr = t3;
  let t4;
  if ($[4] !== dateStr) {
    t4 = dateStr ? <span className="text-[10px] font-medium text-muted-foreground tabular-nums">{dateStr}</span> : null;
    $[4] = dateStr;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const dateRow = t4;
  if (statusType === "LIVE") {
    const minute = event.timing?.currentMinute;
    if (sport === "football") {
      let t5;
      if ($[6] !== event.status || $[7] !== labels.livescore || $[8] !== minute) {
        t5 = formatFootballLiveMinute(event.status, minute, labels.livescore);
        $[6] = event.status;
        $[7] = labels.livescore;
        $[8] = minute;
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      const display = t5;
      let t6;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <span className="font-bold text-green-500 animate-pulse">LIVE</span>;
        $[10] = t6;
      } else {
        t6 = $[10];
      }
      let t7;
      if ($[11] !== display) {
        t7 = display && <span className="text-muted-foreground tabular-nums">{display}</span>;
        $[11] = display;
        $[12] = t7;
      } else {
        t7 = $[12];
      }
      let t8;
      if ($[13] !== dateRow || $[14] !== t7) {
        t8 = <div className="flex flex-col items-center text-xs gap-0.5">{dateRow}{t6}{t7}</div>;
        $[13] = dateRow;
        $[14] = t7;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      return t8;
    }
    const showMinute = minute !== undefined && sport !== "tennis" && sport !== "hockey";
    let t5;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <span className="font-bold text-orange-400 animate-pulse">LIVE</span>;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== minute || $[18] !== showMinute) {
      t6 = showMinute && <span className="text-muted-foreground tabular-nums">{minute}'</span>;
      $[17] = minute;
      $[18] = showMinute;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== dateRow || $[21] !== t6) {
      t7 = <div className="flex flex-col items-center text-xs gap-0.5">{dateRow}{t5}{t6}</div>;
      $[20] = dateRow;
      $[21] = t6;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    return t7;
  }
  if (statusType === "FINISHED") {
    const baseLabel = sport === "football" ? labels.livescore.ft : labels.livescore.fin;
    const finishedLabel = postRegLabel ?? baseLabel;
    let t5;
    if ($[23] !== finishedLabel) {
      t5 = <span className="font-medium text-muted-foreground">{finishedLabel}</span>;
      $[23] = finishedLabel;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    let t6;
    if ($[25] !== dateRow || $[26] !== t5) {
      t6 = <div className="flex flex-col items-center text-xs gap-0.5">{dateRow}{t5}</div>;
      $[25] = dateRow;
      $[26] = t5;
      $[27] = t6;
    } else {
      t6 = $[27];
    }
    return t6;
  }
  let t5;
  if ($[28] !== timeStr) {
    t5 = timeStr && <span className="font-medium text-foreground tabular-nums">{timeStr}</span>;
    $[28] = timeStr;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] !== dateRow || $[31] !== t5) {
    t6 = <div className="flex flex-col items-center text-xs gap-0.5">{dateRow}{t5}</div>;
    $[30] = dateRow;
    $[31] = t5;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  return t6;
}